import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../../firebase';
import { collection, addDoc, getDocs, serverTimestamp } from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';
import Layout from '../../components/ui/Layout';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Avatar from '../../components/ui/Avatar';
import { toast } from '../../components/ui/Toast';
import {
  IconArrowLeft,
  IconCheck,
  IconPlus,
  IconX,
} from '@tabler/icons-react';

export default function ShiftNew() {
  const navigate = useNavigate();
  const { orgId, userProfile, role } = useAuth();
  const [sites, setSites] = useState([]);
  const [staff, setStaff] = useState([]);
  const [machines, setMachines] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [startTime, setStartTime] = useState('07:00');
  const [endTime, setEndTime] = useState('17:00');
  const [siteId, setSiteId] = useState(userProfile?.siteId || '');
  const [assignments, setAssignments] = useState([]);
  const [notes, setNotes] = useState('');

  useEffect(() => {
    const load = async () => {
      if (!orgId) return;
      try {
        const [sitesSnap, staffSnap, machinesSnap] = await Promise.all([
          getDocs(collection(db, 'organisations', orgId, 'sites')),
          getDocs(collection(db, 'organisations', orgId, 'users')),
          getDocs(collection(db, 'organisations', orgId, 'machines')),
        ]);
        setSites(sitesSnap.docs.map(d => ({ id: d.id, ...d.data() })));
        setStaff(staffSnap.docs.map(d => ({ id: d.id, ...d.data() })).filter(u => u.role === 'operator'));
        setMachines(machinesSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (err) {
        console.error(err);
      }
    };
    load();
  }, [orgId]);

  const siteStaff = staff.filter(u => !siteId || u.siteId === siteId);
  const siteMachines = machines.filter(m => !siteId || m.siteId === siteId);

  const addAssignment = (member) => {
    if (assignments.find(a => a.userId === member.id)) return;
    setAssignments(prev => [...prev, {
      userId: member.id,
      userName: member.name,
      machineId: null,
      machineName: null,
      checkedIn: false,
      checkedInAt: null,
      checkedOut: false,
      checkedOutAt: null,
    }]);
  };

  const removeAssignment = (userId) => {
    setAssignments(prev => prev.filter(a => a.userId !== userId));
  };

  const updateAssignmentMachine = (userId, machineId) => {
    const machine = machines.find(m => m.id === machineId);
    setAssignments(prev => prev.map(a =>
      a.userId === userId ? {
        ...a,
        machineId: machineId || null,
        machineName: machine ? `${machine.makeLabel} ${machine.modelLabel}` : null,
      } : a
    ));
  };

  const handleSave = async () => {
    if (!date) { setError('Please select a date'); return; }
    if (!startTime || !endTime) { setError('Please enter start and end times'); return; }
    if (!siteId) { setError('Please select a site'); return; }

    setSaving(true);
    setError('');

    try {
      const site = sites.find(s => s.id === siteId);
      await addDoc(collection(db, 'organisations', orgId, 'shifts'), {
        date,
        startTime,
        endTime,
        siteId,
        siteName: site?.name || null,
        assignments,
        notes: notes.trim() || null,
        status: 'scheduled',
        createdBy: userProfile?.id,
        createdByName: userProfile?.name,
        createdAt: serverTimestamp(),
      });
      toast.success('Shift created');
      navigate('/shifts');
    } catch (err) {
      console.error(err);
      setError('Failed to create shift — please try again');
    }
    setSaving(false);
  };

  return (
    <Layout>
      <div style={{ padding: 'var(--main-padding)', maxWidth: 640, margin: '0 auto' }}>

        <div
          onClick={() => navigate('/shifts')}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 13, color: 'var(--color-text-secondary)', cursor: 'pointer', marginBottom: 20 }}
        >
          <IconArrowLeft size={15} stroke={1.5} /> Shifts
        </div>

        <h1 style={{ fontSize: 22, fontWeight: 400, marginBottom: 24 }}>Create shift</h1>

        {error && (
          <div style={{ background: 'var(--color-danger-light)', border: '0.5px solid var(--color-danger)', borderRadius: 'var(--radius-md)', padding: '10px 14px', fontSize: 13, color: 'var(--color-danger)', marginBottom: 16 }}>
            {error}
          </div>
        )}

        {/* Date & time */}
        <Card style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 14, fontWeight: 400, marginBottom: 14 }}>Date & time</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
            <div>
              <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 6 }}>
                Date <span style={{ color: 'var(--color-danger)' }}>*</span>
              </label>
              <input
                type="date"
                value={date}
                onChange={e => setDate(e.target.value)}
                style={{ width: '100%', boxSizing: 'border-box' }}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 6 }}>
                Start time <span style={{ color: 'var(--color-danger)' }}>*</span>
              </label>
              <input
                type="time"
                value={startTime}
                onChange={e => setStartTime(e.target.value)}
                style={{ width: '100%', boxSizing: 'border-box' }}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 6 }}>
                End time <span style={{ color: 'var(--color-danger)' }}>*</span>
              </label>
              <input
                type="time"
                value={endTime}
                onChange={e => setEndTime(e.target.value)}
                style={{ width: '100%', boxSizing: 'border-box' }}
              />
            </div>
          </div>
        </Card>

        {/* Site */}
        <Card style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 14, fontWeight: 400, marginBottom: 14 }}>Site</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {sites.map(site => (
              <div
                key={site.id}
                onClick={() => { setSiteId(site.id); setAssignments([]); }}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '10px 14px', borderRadius: 'var(--radius-md)',
                  border: '0.5px solid',
                  borderColor: siteId === site.id ? 'var(--color-brand)' : 'var(--color-border-light)',
                  background: siteId === site.id ? 'var(--color-brand-light)' : 'transparent',
                  cursor: 'pointer', transition: 'all 0.15s',
                }}
              >
                <span style={{ fontSize: 13, color: siteId === site.id ? 'var(--color-brand)' : 'var(--color-text-primary)' }}>
                  {site.name}
                </span>
                {siteId === site.id && <IconCheck size={15} color="var(--color-brand)" stroke={2} />}
              </div>
            ))}
          </div>
        </Card>

        {/* Staff assignments */}
        <Card style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 14, fontWeight: 400, marginBottom: 14 }}>Staff assignments</div>

          {assignments.length > 0 && (
            <div style={{ marginBottom: 14 }}>
              {assignments.map(a => (
                <div key={a.userId} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '0.5px solid var(--color-border-light)' }}>
                  <Avatar name={a.userName} size="sm" />
                  <div style={{ flex: 1, fontSize: 13, color: 'var(--color-text-primary)' }}>{a.userName}</div>
                  <select
                    value={a.machineId || ''}
                    onChange={e => updateAssignmentMachine(a.userId, e.target.value)}
                    style={{ fontSize: 12, padding: '5px 8px', borderRadius: 'var(--radius-md)', border: '0.5px solid var(--color-border-medium)', background: '#fff', maxWidth: 160 }}
                  >
                    <option value="">No machine</option>
                    {siteMachines.map(m => (
                      <option key={m.id} value={m.id}>{m.makeLabel} {m.modelLabel}</option>
                    ))}
                  </select>
                  <div
                    onClick={() => removeAssignment(a.userId)}
                    style={{ cursor: 'pointer', color: 'var(--color-text-tertiary)', display: 'flex' }}
                  >
                    <IconX size={15} stroke={1.5} />
                  </div>
                </div>
              ))}
            </div>
          )}

          <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', marginBottom: 10 }}>
            {siteId ? `${siteStaff.length} operator${siteStaff.length !== 1 ? 's' : ''} at this site` : 'Select a site to see operators'}
          </div>

          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {siteStaff.filter(u => !assignments.find(a => a.userId === u.id)).map(member => (
              <div
                key={member.id}
                onClick={() => addAssignment(member)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  padding: '5px 10px', borderRadius: 'var(--radius-full)',
                  border: '0.5px solid var(--color-border-medium)',
                  cursor: 'pointer', fontSize: 12, color: 'var(--color-text-secondary)',
                  transition: 'all 0.15s',
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--color-brand)'; e.currentTarget.style.color = 'var(--color-brand)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--color-border-medium)'; e.currentTarget.style.color = 'var(--color-text-secondary)'; }}
              >
                <IconPlus size={11} stroke={2} />
                {member.name.split(' ')[0]}
              </div>
            ))}
          </div>
        </Card>

        {/* Notes */}
        <Card style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 14, fontWeight: 400, marginBottom: 14 }}>Notes</div>
          <textarea
            placeholder="Any shift notes or instructions..."
            value={notes}
            onChange={e => setNotes(e.target.value)}
            rows={3}
            style={{ width: '100%', boxSizing: 'border-box', resize: 'vertical', fontFamily: 'inherit' }}
          />
        </Card>

        <div style={{ display: 'flex', gap: 10 }}>
          <Button variant="secondary" onClick={() => navigate('/shifts')}>Cancel</Button>
          <Button
            variant="primary"
            fullWidth
            onClick={handleSave}
            disabled={saving}
            loading={saving}
            icon={<IconCheck size={14} stroke={1.5} />}
          >
            {saving ? 'Saving...' : 'Create shift'}
          </Button>
        </div>

      </div>
    </Layout>
  );
}